import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Input, Label, Stack } from "@altimateai/lego";
import { FormSubmit } from "./components/form";
import { BaseContainer, Header } from "./components/base";
import { validateEmail } from "./helper";
import { useMutation } from "@tanstack/react-query";
import { getEmailInstanceList } from "../../helpers/apis";
import classNames from "classnames";
import styles from "./components/styles.module.scss";
import { InstanceList, Instance } from "./components/InstanceList";
import { isMobile } from "@/helpers/utils";

const EMAIL_INPUT_ID = "email-input";

const FineLine = () => (
  <div style={{ width: "100%", height: "100%", border: "1px #D5DBE4 solid" }}></div>
);

const OrLine = () => (
  <Stack align="center" gap="xl">
    <FineLine />
    <div className="text-muted">or</div>
    <FineLine />
  </Stack>
);

const FindInstance = () => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [instanceList, setInstanceList] = useState<Instance[]>([]);
  const [showInstances, setShowInstances] = useState(false);

  const [activeElement, setActiveElement] = useState<HTMLElement | null>(null);
  const onFocusChange = (e: React.FocusEvent<HTMLInputElement>) => {
    setActiveElement(e.target);
  };

  const { mutate: companyList } = useMutation({
    mutationFn: getEmailInstanceList,
    onSuccess: response => {
      if (response) {
        if (!response.companies?.length) {
          setEmailError("This email is not associated with any instance.");
        } else if (response.companies.length === 1) {
          window.location.href = `${response.companies[0].company_url}?user=${email}`;
        } else {
          setInstanceList(
            response.companies.map(company => ({
              url: `${company.company_url}?user=${email}`,
              name: company.company.name,
              creationTime: company.company.existence_time,
            }))
          );
          setShowInstances(true);
          setEmail("");
        }
      }
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const error = validateEmail(email);
    if (error) {
      setEmailError(error);
    } else {
      setEmailError("");
      companyList(email);
    }
  };

  const isMobileView = isMobile();

  return (
    <BaseContainer
      footer={
        !showInstances && (
          <div>
            <span className="text-muted fw-400">Don&apos;t have an instance? Please sign up </span>
            <Link to="https://app.myaltimate.com/register" className="text-primary">
              here
            </Link>
            .
          </div>
        )
      }
    >
      <div className={classNames({ [styles.mobile_login]: isMobileView })}>
        <Header>{showInstances ? "Select your Instance" : "Enter your Email"}</Header>
        {showInstances ? (
          <InstanceList instances={instanceList} />
        ) : (
          <div className={styles.sub_header}>
            You need to login via the instance-specific URL. Get redirected to the correct URL by
            entering the email used for the sign-up.
          </div>
        )}
        {showInstances && <OrLine />}
        <form onSubmit={handleSubmit} className={`${isMobileView ? "al-w-full mt-3" : "mt-4"}`}>
          <div className="mb-3">
            <Label className="form-label" htmlFor="email">
              {showInstances ? "Try with another email" : "Email"}
            </Label>
            <Stack direction="column" gap="none">
              <div
                className={classNames(styles.tenant_form_input, {
                  [styles.in_focus]: activeElement?.id === EMAIL_INPUT_ID,
                  [styles.is_invalid]: emailError !== "",
                })}
              >
                <Input
                  id={EMAIL_INPUT_ID}
                  name="email"
                  type="email"
                  placeholder="Enter email"
                  onChange={event => setEmail(event.target.value)}
                  onFocus={onFocusChange}
                  onBlur={e => setEmailError(validateEmail(e.target.value.toLowerCase()))}
                  value={email.toLowerCase()}
                />
              </div>
              {emailError !== "" && (
                <div
                  className={classNames(styles.form_feedback, {
                    [styles.is_error]: emailError !== "",
                  })}
                >
                  <span>{emailError}</span>
                </div>
              )}
            </Stack>
          </div>

          <div className="mt-4">
            <FormSubmit disabled={!email || emailError !== ""}>Continue</FormSubmit>
          </div>
        </form>
      </div>
    </BaseContainer>
  );
};

export { FindInstance };
